import { useState, useEffect } from 'react';
import { Mail, Loader, AlertCircle, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { useBrandIdentity } from '../../contexts/BrandIdentityContext';
import { authService } from '../../services/PlatformService';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, login } = useAuth();
  const { colors: brandColors } = useBrandIdentity();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loginMode, setLoginMode] = useState('email'); // 'email' or 'password'

  useEffect(() => {
    if (currentUser) {
      const from = location.state?.from?.pathname || '/contents';
      navigate(from, { replace: true });
    }
  }, [currentUser, navigate, location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (error) {
      setError('');
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (!formData.email) {
      setError('Email é obrigatório');
      return;
    }

    if (loginMode === 'email') {
      setLoginMode('password');
      return;
    }

    if (!formData.password) {
      setError('Senha é obrigatória');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await authService.login(formData.email, formData.password);
      if (response && response.data) {
        login(response.data.user, response.data.token);
        const from = location.state?.from?.pathname || '/contents';
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.log(error);
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError('Não foi possível fazer login. Por favor, verifique suas credenciais e tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex flex-col">

      {/* Top Bar - Voltar */}
      <div className="w-full px-4 pt-4">
        <div className="max-w-md mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-8 px-4">
        <div className="max-w-md w-full">
          
          {/* Logo e Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img
                src="/logo.png"
                alt="hello, social"
                className="w-24 h-24 object-contain mx-auto"
              />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Entre na sua conta
            </h1>
            <p className="text-base text-gray-600">
              Acesse sua conta para criar conteúdos incríveis
            </p>
          </div>

          {/* Card de Login */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all">
            
            <form onSubmit={handleEmailLogin} className="space-y-4">
              {/* Mostrar erro */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                    <Mail className="w-4 h-4" />
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all"
                    placeholder="Digite seu e-mail"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              {loginMode === 'password' && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Senha
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-gray-500 hover:text-amber-600 transition-colors hover:underline"
                    >
                      Esqueci minha senha
                    </Link>
                  </div>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <Lock className="w-4 h-4" />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full pl-10 pr-10 py-3 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all"
                      placeholder="Digite sua senha"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              {/* Login Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-white font-semibold hover:from-amber-500 hover:to-orange-600 transition-all transform hover:scale-[1.02] shadow-lg focus:outline-none focus:ring-4 focus:ring-amber-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    loginMode === 'email' ? 'Continuar' : 'Entrar'
                  )}
                </button>
              </div>

              {/* Link para entrar com senha */}
              {loginMode === 'email' && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setLoginMode('password')}
                    className="text-sm text-amber-600 hover:text-amber-700 transition-colors hover:underline font-medium"
                  >
                    Entrar com senha
                  </button>
                </div>
              )}

              {/* Link para cadastro */}
              <div className="text-center pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  Não tem uma conta?{' '}
                  <Link 
                    to="/register" 
                    className="font-semibold text-amber-600 hover:text-amber-700 transition-colors hover:underline"
                  >
                    Criar conta grátis
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Footer com vantagens */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-white rounded-full border border-gray-200 text-xs text-gray-600 shadow-sm">
              <span className="text-amber-500 font-semibold">✨</span>
              <span>Grátis para começar</span>
              <span className="text-gray-300">•</span>
              <span>Sem cartão necessário</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;